import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins, Plus, Zap, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

interface BuyCreditsWidgetProps {
    expanded: boolean;
}

const BuyCreditsWidget: React.FC<BuyCreditsWidgetProps> = ({ expanded }) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    if (user?.isAdmin) return null;

    if (!expanded) {
        return (
            <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/billing')}
                className="w-12 h-12 mx-auto mb-8 flex items-center justify-center rounded-2xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all relative group"
            >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 to-fuchsia-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <Coins size={20} className="text-violet-600 relative z-10" />
                
                {/* Active Indicator */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-fuchsia-500 border-2 border-white rounded-full shadow-sm" />
            </motion.button>
        );
    }

    return (
        <motion.div 
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-4 pb-10 mt-auto"
        >
            <div className="relative p-6 rounded-[2rem] bg-slate-950 border border-white/10 shadow-2xl overflow-hidden group">
                {/* Subtle Modern Glows */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-violet-600/20 blur-[60px] rounded-full group-hover:bg-violet-600/30 transition-colors duration-700" />
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-fuchsia-600/10 blur-[60px] rounded-full group-hover:bg-fuchsia-600/20 transition-colors duration-700" />
                
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 shadow-inner">
                                <Zap size={16} className="text-fuchsia-400 fill-fuchsia-400/20" />
                            </div>
                            <span className="text-[10px] font-bold text-white uppercase tracking-[0.15em]">Credits</span>
                        </div>
                        <div className="bg-fuchsia-500/10 border border-fuchsia-500/20 px-2.5 py-1 rounded-lg">
                             <span className="text-sm font-bold text-fuchsia-400 tabular-nums">{user?.creditsRemaining || 0}</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-[15px] font-bold text-white tracking-tight leading-tight">Fast-track your data leads.</h4>
                        
                        <button
                            onClick={() => navigate('/billing')}
                            className="w-full h-11 bg-white hover:bg-slate-50 text-slate-950 rounded-xl font-bold text-[13px] transition-all flex items-center justify-between px-5 group/btn active:scale-[0.98] shadow-lg shadow-white/5"
                        >
                            <span className="flex items-center gap-2">
                                <Plus size={16} strokeWidth={2.5} />
                                Buy Credits
                            </span>
                            <ArrowRight size={14} className="opacity-0 group-hover/btn:opacity-100 group-hover/btn:translate-x-1 transition-all" />
                        </button>
                    </div>
                </div>

                {/* Animated progress-style line at the bottom */}
                <div className="absolute bottom-0 left-0 h-[1.5px] w-full bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
            </div>
        </motion.div>
    );
};

export default BuyCreditsWidget;
